import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import TrackerShell from "../../components/tracker/TrackerShell";
import { db } from "../../firebase";
import {
  collection,
  onSnapshot,
  updateDoc,
  doc,
  serverTimestamp,
  query,
  orderBy,
  getDocs,
  startAfter,
  limit,
  setDoc,
  getDoc,
} from "firebase/firestore";

import "../MyTeam/shared-tracker-styles.css";
import "../MyTeam/licensing-notes-modal.css";

import UserProfileCard from "../../components/profile/UserProfileCard";
import useProfileModalRoute from "../../components/profile/useProfileModalRoute";
import { auth } from "../../firebase";

// Centralized notes service
import {
  addNote,
  deleteNote as deleteNoteService,
  subscribeToProspectNotes,
} from "../../services/notesService";

import { useUIVersion } from "../../contexts/UIVersionContext";
import { useAuth } from "../../hooks/useAuth";
import { ACCOUNT_TYPES, useAccountType } from "../../hooks/useAccountType";
import { useDebounce } from "../../hooks/useDebounce";
import {
  TrackerContainer,
  TrackerNotes,
  TrackerFilters,
  filterByDateRange,
  filterByBaseShop,
} from "../../components/tracker-ui";
import { IconUsers, IconLicense } from "@tabler/icons-react";

import {
  TooltipProvider,
  Tooltip,
  TooltipTrigger,
  TrackerTooltip,
} from "../../components/ui/tooltip";
import "../../components/ui/tooltip-styles.css";

// ✅ Import trackerSync functions for proper date mirroring
import {
  updateLicensingDate as syncUpdateLicensingDate,
  updateLicensingField as syncUpdateLicensingField,
} from "./services/trackerSync";

/* =========================
   Firestore Refs & Constants
   ========================= */
const trackersRef = (userId) => doc(db, "trackers", userId);
const licensingRef = (userId) => doc(db, "licensing", userId);

// Hierarchy Filtering
const DOWNLINE_PLANS = new Set([
  ACCOUNT_TYPES.LEADER,
  ACCOUNT_TYPES.BROKER,
  ACCOUNT_TYPES.SENIOR_BROKER,
  ACCOUNT_TYPES.ADMIN,
]);

function buildAccessibleUsers(plan, allUsers, currentUserId) {
  if (!currentUserId) return new Set();

  // New Agent / Agent: only their own data
  if (!DOWNLINE_PLANS.has(plan)) {
    return new Set([currentUserId]);
  }

  // Admin: can monitor everything
  if (plan === ACCOUNT_TYPES.ADMIN) {
    const all = new Set([currentUserId]);
    allUsers.forEach((u) => all.add(u.id));
    return all;
  }

  // Leaders/Brokers/Senior Brokers: their users + full downline (via recruiterId chain)
  const childrenMap = new Map();
  allUsers.forEach((u) => {
    if (!u?.recruiterId) return;
    if (!childrenMap.has(u.recruiterId)) childrenMap.set(u.recruiterId, []);
    childrenMap.get(u.recruiterId).push(u.id);
  });

  const allowed = new Set([currentUserId]);
  const stack = [currentUserId];
  while (stack.length) {
    const node = stack.pop();
    const kids = childrenMap.get(node) || [];
    kids.forEach((kid) => {
      if (!allowed.has(kid)) {
        allowed.add(kid);
        stack.push(kid);
      }
    });
  }

  return allowed;
}

/* =========================
   Helper functions for notes
   ========================= */
function uniqueId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID)
    return crypto.randomUUID();
  return `id_${Date.now()}_${Math.round(Math.random() * 1e9)}`;
}

function formatDateTimePretty(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
  const time = d
    .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    .toLowerCase();
  return `${date} at ${time}`;
}

function tsToMs(v) {
  if (!v) return 0;
  if (typeof v?.toMillis === "function") return v.toMillis();
  if (v instanceof Date) return v.getTime();
  const t = new Date(v).getTime();
  return Number.isFinite(t) ? t : 0;
}

function formatDateTimeISO(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
  const time = d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date} ${time}`;
}

// Default licensing structure (used ONLY if the licensing sub-object doesn't exist yet)
const DEFAULT_LICENSING = {
  applicationSubmitted: false,
  attendedClass: false,
  completedCourse: false,
  fingerprint: false,
  licenseActive: false,
  passedExam: false,
  practiceExam: false,
  scheduledClass: false,
  scheduledExam: false,
  startedCourse: false,
  xcel: false,
  recruiter: "",
  email: "",
  leader: "",
  date: null,
  notes: {},
  createdAt: serverTimestamp(),
  updatedAt: serverTimestamp(),
};

/* =========================
   Helper functions
   ========================= */
// Safe leaf-field update function - writes to both trackers and licensing collections
const updateLicensingField = async (userId, fieldPath, value) => {
  try {
    // Update in trackers collection
    await updateDoc(trackersRef(userId), {
      [fieldPath]: value,
      lastUpdatedBy: "licensing",
      updatedAt: serverTimestamp(),
    });

    // Also update in standalone licensing collection
    // Remove 'licensing.' prefix if present for the standalone collection
    const licensingFieldPath = fieldPath.replace(/^licensing\./, '');
    await setDoc(
      licensingRef(userId),
      {
        [licensingFieldPath]: value,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error("Error updating licensing field:", error);
    throw error;
  }
};

/**
 * Initialize the licensing sub-object ONLY if it doesn't exist.
 * DO NOT overwrite the entire "licensing" object during normal toggles.
 */
const ensureLicensingExists = async (userId, rowData = {}) => {
  try {
    const ref = trackersRef(userId);
    const snap = await getDoc(ref);

    // If doc has a licensing object already, do nothing
    if (snap.exists() && snap.data()?.licensing) return;

    // Initialize licensing once
    const defaultWithData = {
      ...DEFAULT_LICENSING,
      email: rowData.email || "",
      leader: rowData.leader || "",
      date: rowData.date || null,
    };

    await setDoc(
      ref,
      {
        licensing: defaultWithData,
        updatedAt: serverTimestamp(),
        lastUpdatedBy: "licensing",
      },
      { merge: true }
    );
  } catch (error) {
    console.error("Error ensuring licensing exists:", error);
    throw error;
  }
};

// Simple leaf toggle (no recompute here; isolates the xcel/fingerprint issue)
const updateCheckboxField = async (userId, fieldName, value, rowData) => {
  try {
    await ensureLicensingExists(userId, rowData);
    await updateLicensingField(userId, `licensing.${fieldName}`, value);
  } catch (error) {
    console.error(`Error updating ${fieldName}:`, error);
    throw error;
  }
};

/* ------------------ Click-to-open Info Popover ------------------ */
function InfoPopoverButton({ text }) {
  const [open, setOpen] = React.useState(false);
  const btnRef = React.useRef(null);
  const [pos, setPos] = React.useState({ top: 0, left: 0 });

  const toggle = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (r) {
      const maxWidth = 340;
      const left = Math.min(
        Math.max(12, r.left),
        window.innerWidth - maxWidth - 12
      );
      setPos({ top: r.bottom + 8, left });
    }
    setOpen((v) => !v);
  };

  React.useEffect(() => {
    const onDown = (e) => {
      if (!open) return;
      const pop = document.getElementById("lic-info-popover");
      if (
        pop &&
        !pop.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onEsc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        className="mini-btn info-btn"
        aria-expanded={open ? "true" : "false"}
        aria-label="Column information"
        title="Info"
      >
        <span className="info-dot" aria-hidden>
          i
        </span>
      </button>

      {open && (
        <div
          id="lic-info-popover"
          className="lic-info-popover"
          style={{ top: pos.top, left: pos.left }}
        >
          <div className="lic-info-card">{text}</div>
        </div>
      )}
    </>
  );
}

/* ------------------ Column Tooltips ------------------ */
const COLUMN_TOOLTIPS = {
  xcel: "Xcel pre-licensing completed.",
  testDate: "Date the licensing test is taken/scheduled.",
  testResult: "Enter the date of the result and mark Passed: Yes/No.",
  fingerprint: "Fingerprint completed.",
  sirconNiprDate: "Sircon/NIPR registration date.",
  licenseCert: "License certificate received.",
  launchDirect: "Launch/Direct appointment set.",
  agentAgreement: "Agent agreement completed.",
  agentApprovalDate: "Agent approval date.",
  continuingEd: "Continuing education complete.",
  eopPlatform: "E&O and platform setup.",
  directDeposit: "Direct deposit setup.",
  licensed: "Turns green automatically when ALL requirements are completed.",
  notes: "Additional notes.",
};

/* ------------------ Helpers ------------------ */
const toInputDate = (val) => {
  if (!val) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const da = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${da}`;
  } catch {
    return "";
  }
};

const fmtDate = (iso) =>
  !iso
    ? ""
    : new Date(iso).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "2-digit",
      });

function displayName(row) {
  const combo = [row.firstName, row.lastName].filter(Boolean).join(" ");
  return row.name || row.fullName || combo || "—";
}

function initialsFrom(name) {
  const n = (name || "").trim();
  if (!n) return "?";
  const p = n.split(/\s+/);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase();
}

/* Avatar */
function AvatarSquare({ row, size = 30, onClick }) {
  const url = row.photoURL || row.avatarUrl || row.photo;
  const txt = initialsFrom(displayName(row));
  return url ? (
    <img
      src={url}
      alt={displayName(row)}
      className="avatar-square"
      style={{
        width: size,
        height: size,
        borderRadius: 8,
        cursor: "pointer",
        objectFit: "cover",
      }}
      onClick={onClick}
    />
  ) : (
    <div
      className="avatar-fallback"
      style={{
        width: size,
        height: size,
        borderRadius: 8,
        cursor: "pointer",
        background: "rgba(255,255,255,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: "bold",
        color: "#f3d67a",
        fontSize: "12px",
      }}
      onClick={onClick}
      title="No profile photo"
    >
      {txt}
    </div>
  );
}

/* ------------------ Resizable Grid ------------------ */
function ResizableGrid({
  columns,
  rows,
  loading,
  emptyText,
  renderCell,
  rowKey,
  lockableLeftCount = 4,
  initialLockedKeys = ["index", "name", "recruiter", "leader"],
  rowStyle,
  sortKey,
  sortDir,
  onSort,
  renderHeaderSearch,
}) {
  const storageKey = "tracker-licensing-columns";

  const [colWidths, setColWidths] = useState(() =>
    Object.fromEntries(columns.map((c) => [c.key, c.width || 140]))
  );

  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const savedWidths = JSON.parse(saved);
        setColWidths((w) => ({ ...w, ...savedWidths }));
      }
    } catch (e) {
      console.warn("Failed to load licensing column widths:", e);
    }
  }, []);

  const [lockedKeys, setLockedKeys] = useState(new Set(initialLockedKeys));
  const lockedColumns = useMemo(
    () => columns.filter((c) => lockedKeys.has(c.key)),
    [columns, lockedKeys]
  );
  const scrollableColumns = useMemo(
    () => columns.filter((c) => !lockedKeys.has(c.key)),
    [columns, lockedKeys]
  );
  const lockedBlockWidth = useMemo(
    () =>
      lockedColumns.reduce(
        (sum, col) => sum + (colWidths[col.key] ?? (col.width || 140)),
        0
      ),
    [lockedColumns, colWidths]
  );

  const dragRef = useRef(null);
  
    // Refs for scroll synchronization
    const lockedSectionRef = useRef(null);
    const scrollSectionRef = useRef(null);

    // Sync scrolling between locked and scrollable sections
    useEffect(() => {
      const lockedEl = lockedSectionRef.current;
      const scrollEl = scrollSectionRef.current;
      if (!lockedEl || !scrollEl) return;

      let isSyncing = false; // Flag to prevent infinite loop

      const handleLockedScroll = () => {
        if (!isSyncing) {
          isSyncing = true;
          scrollEl.scrollTop = lockedEl.scrollTop;
          requestAnimationFrame(() => {
            isSyncing = false;
          });
        }
      };

      const handleScrollScroll = () => {
        if (!isSyncing) {
          isSyncing = true;
          lockedEl.scrollTop = scrollEl.scrollTop;
          requestAnimationFrame(() => {
            isSyncing = false;
          });
        }
      };

      lockedEl.addEventListener('scroll', handleLockedScroll, { passive: true });
      scrollEl.addEventListener('scroll', handleScrollScroll, { passive: true });

      return () => {
        lockedEl.removeEventListener('scroll', handleLockedScroll);
        scrollEl.removeEventListener('scroll', handleScrollScroll);
      };
    }, [lockedColumns.length]);
  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current) return;
      const { key, startX, startW } = dragRef.current;
      const dx = e.clientX - startX;
      const next = Math.max(80, startW + dx);
      setColWidths((w) => ({ ...w, [key]: next }));
    };
    const onUp = () => {
      dragRef.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      try {
        localStorage.setItem(storageKey, JSON.stringify(colWidths));
      } catch {}
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [colWidths]);

  const startDrag = (key, e) => {
    dragRef.current = { key, startX: e.clientX, startW: colWidths[key] || 140 };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  const toggleLock = (key) => {
    setLockedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderHeaderCommon = (c, isLocked) => {
    const w = colWidths[c.key];
    const isSorted = c.sortable && c.key === sortKey;
    const sortLabel = isSorted ? (sortDir === "asc" ? "▲" : "▼") : "↕";
    const originalIndex = columns.findIndex((col) => col.key === c.key);

    return (
      <div
        key={c.key}
        className={`rg-cell rg-head ${isLocked ? "locked" : ""}`}
        style={{ width: w, minWidth: w, maxWidth: w }}
      >
        <div className="rg-head-inner">
          <div className="rg-head-top">
            <div className="rg-title-group">
              {originalIndex < lockableLeftCount && c.showLock !== false && (
                <button
                  type="button"
                  className={`lock-btn ${lockedKeys.has(c.key) ? "on" : ""}`}
                  onClick={() => toggleLock(c.key)}
                  title={
                    lockedKeys.has(c.key) ? "Unlock column" : "Lock column"
                  }
                >
                  {lockedKeys.has(c.key) ? "🔒" : "🔓"}
                </button>
              )}
              <span className="rg-title">{c.label}</span>
              {COLUMN_TOOLTIPS[c.key] && (
                <InfoPopoverButton text={COLUMN_TOOLTIPS[c.key]} />
              )}
            </div>
            <div className="rg-actions-group">
              {c.sortable && (
                <button
                  type="button"
                  className={`mini-btn sort-btn ${isSorted ? "on" : ""}`}
                  onClick={() => onSort && onSort(c.key)}
                  title={isSorted ? `Sorted ${sortDir}` : "Sort"}
                >
                  {sortLabel}
                </button>
              )}
            </div>
          </div>
          <div className="rg-head-search">
            {renderHeaderSearch ? renderHeaderSearch(c) : null}
          </div>
        </div>
        <span
          className="rg-grip"
          onMouseDown={(e) => startDrag(c.key, e)}
          title="Drag to resize"
        />
      </div>
    );
  };

  const renderLockedHeader = () => (
    <div className="rg-row rg-header" style={{background: "#252830"}}>
      {lockedColumns.map((c) => renderHeaderCommon(c, true))}
    </div>
  );
  const renderScrollableHeader = () => (
    <div className="rg-row rg-header" style={{background: "#252830"}}>
      {scrollableColumns.map((c) => renderHeaderCommon(c, false))}
    </div>
  );

  const renderLockedRows = () => {
    if (loading || !rows.length) return null;
    return rows.map((r, rowIdx) => {
      const rowStyles = typeof rowStyle === "function" ? rowStyle(r) : {};
      return (
        <div
          className="rg-row"
          key={rowKey ? rowKey(r) : rowIdx}
          style={rowStyles}
        >
          {lockedColumns.map((c) => {
            const w = colWidths[c.key];
            return (
              <div
                key={c.key}
                className="rg-cell locked"
                style={{ width: w, minWidth: w, maxWidth: w }}
              >
                {renderCell(r, c, rowIdx)}
              </div>
            );
          })}
        </div>
      );
    });
  };

  const renderScrollableRows = () => {
    if (loading) return <div className="rg-empty">Loading…</div>;
    if (!rows.length)
      return <div className="rg-empty">{emptyText || "No data"}</div>;

    return rows.map((r, rowIdx) => {
      const rowStyles = typeof rowStyle === "function" ? rowStyle(r) : {};
      return (
        <div
          className="rg-row"
          key={rowKey ? rowKey(r) : rowIdx}
          style={rowStyles}
        >
          {scrollableColumns.map((c) => {
            const w = colWidths[c.key];
            return (
              <div
                key={c.key}
                className={`rg-cell ${c.center ? "center" : ""}`}
                style={{ width: w, minWidth: w, maxWidth: w }}
              >
                {renderCell(r, c, rowIdx)}
              </div>
            );
          })}
        </div>
      );
    });
  };

  return (
    <div className="rg-wrap">
      <div className="rg-layout">
        <div
          ref={lockedSectionRef}
          className="rg-locked-section"
          style={{ width: lockedBlockWidth, minWidth: lockedBlockWidth }}
        >
          {lockedColumns.length > 0 && renderLockedHeader()}
          {renderLockedRows()}
        </div>

        <div className="rg-scroll-section">
          <div ref={scrollSectionRef} className="rg-scroll">
            {renderScrollableHeader()}
            {renderScrollableRows()}
          </div>
        </div>
      </div>

      {/* Cell styles */}
      <style>{`
        .light-cell {
          width: 100%;
          height: 38px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          border: 2px solid rgba(255,255,255,.15);
          transition: all 0.3s ease;
          font-weight: bold;
          cursor: pointer;
        }
        .light-cell.red { background: #3a1b1b; border-color: #5a2b2b; color: #ff6b6b; }
        .light-cell.green { background: #0f3d2e; border-color: #2f5d4e; color: #41d27a; }

        .date-input {
          width: 140px;
          background: rgba(255,255,255,.1);
          border: 2px solid rgba(255,255,255,.25);
          color: #fff;
          border-radius: 8px;
          padding: 6px 8px;
          text-align: center;
          font-weight: 600;
          font-size: 14px;
          outline: none;
        }
        .date-input:focus { border-color: #f3d67a; box-shadow: 0 0 10px rgba(243,214,122,.3); }

        .result-wrap { display: flex; gap: 8px; align-items: center; justify-content: center; }
        .yesno-select {
          width: 100px;
          background: rgba(255,255,255,.1);
          border: 2px solid rgba(255,255,255,.3);
          color: #fff;
          border-radius: 8px;
          padding: 6px 8px;
          text-align: center;
          font-weight: bold;
          font-size: 14px;
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .yesno-select:focus { outline: none; border-color: #41d27a; box-shadow: 0 0 10px rgba(65,210,122,0.3); }

        .details-btn {
          width: 100%;
          background: rgba(255,255,255,.08);
          border: 2px solid rgba(255,255,255,.2);
          color: #fff;
          border-radius: 8px;
          padding: 8px 12px;
          font-weight: bold;
          transition: all 0.3s ease;
        }
        .details-btn:hover { background: rgba(255,255,255,.12); border-color: rgba(255,255,255,.3); }

        .license-badge {
          padding: 6px 12px;
          border-radius: 20px;
          font-weight: bold;
          font-size: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .license-badge.yes {
          background: #0f3d2e;
          color: #41d27a;
          border: 2px solid #2f5d4e;
        }
        .license-badge.no {
          background: #3a1b1b;
          color: #ff6b6b;
          border: 2px solid #5a2b2b;
        }

        /* Licensing info popover — always over the table */
        .lic-info-popover {
          position: fixed !important;
          z-index: 1000000 !important; /* higher than any grid element */
          pointer-events: none; /* allow outside click-through to close via handler */
        }
        .lic-info-card {
          pointer-events: auto;
          max-width: 340px;
          background: #1a1a1f;
          border: 1px solid rgba(255,255,255,0.22);
          border-radius: 12px;
          padding: 12px 14px;
          color: #e7ecf7;
          box-shadow: 0 18px 48px rgba(0,0,0,0.6);
          font-size: 13px;
          line-height: 1.35;
        }

        /* Horizontal scrollbar styling */
        .rg-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(255,215,0,.3) rgba(255,255,255,.05);
          -ms-overflow-style: auto;
          overflow-x: auto;
          overflow-y: auto;
        }

        .rg-scroll::-webkit-scrollbar {
          height: 8px;
        }

        .rg-scroll::-webkit-scrollbar-track {
          background: rgba(255,255,255,.05);
        }

        .rg-scroll::-webkit-scrollbar-thumb {
          background: rgba(255,215,0,.3);
          border-radius: 4px;
        }

        .rg-scroll::-webkit-scrollbar-thumb:hover {
          background: rgba(255,215,0,.5);
        }

        .rg-scroll-section {
          overflow-x: auto;
          overflow-y: hidden;
        }

        /* Locked section vertical scrolling */
        .rg-locked-section {
          overflow-y: auto;
          overflow-x: hidden;
        }

        /* Ensure locked and scroll sections are aligned */
        .rg-row {
          display: flex;
          align-items: stretch;
        }

        .rg-row:not(.rg-header) {
          height: 64px;
        }
        .rg-header {
            min-height: 125px;
            background: #1a1a1f !important;
        }
        
        .rg-cell {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          box-sizing: border-box;
        }
      `}</style>
    </div>
  );
}

/* ------------------ Columns ------------------ */
// KEEPING ORIGINAL COLUMN STRUCTURE
const COLS = [
  { key: "index", label: "#", width: 40, showLock: false },
  { key: "name", label: "Name", sortable: true, width: 260 },
  { key: "recruiter", label: "Recruiter", sortable: true, width: 200 },
  { key: "leader", label: "Leader", sortable: true, width: 200 },

  { key: "xcel", label: "Xcel", type: "check", center: true, width: 120, sortable: true },

  {
    key: "testDate",
    label: "Test Date",
    type: "date",
    center: true,
    width: 160,
    sortable: true,
  },
  {
    key: "testResult",
    label: "Test Result",
    type: "resultCombo",
    center: true,
    width: 260,
    sortable: true,
  },

  {
    key: "fingerprint",
    label: "Fingerprint",
    type: "check",
    center: true,
    width: 140,
    sortable: true,
  },

  {
    key: "sirconNiprDate",
    label: "Sircon / NIPR",
    type: "date",
    center: true,
    width: 170,
    sortable: true,
  },

  {
    key: "licenseCert",
    label: "Lic. Certificate",
    type: "check",
    center: true,
    width: 160,
    sortable: true,
  },
  {
    key: "launchDirect",
    label: "Launch / Direct",
    type: "check",
    center: true,
    width: 160,
    sortable: true,
  },
  {
    key: "agentAgreement",
    label: "Agent Agreement",
    type: "check",
    center: true,
    width: 160,
    sortable: true,
  },

  {
    key: "agentApprovalDate",
    label: "Agent Approval",
    type: "date",
    center: true,
    width: 170,
    sortable: true,
  },

  {
    key: "continuingEd",
    label: "Cont. Education",
    type: "check",
    center: true,
    width: 160,
    sortable: true,
  },
  {
    key: "eopPlatform",
    label: "E&O & Platform",
    type: "check",
    center: true,
    width: 160,
    sortable: true,
  },
  {
    key: "directDeposit",
    label: "Direct Deposit",
    type: "check",
    center: true,
    width: 160,
    sortable: true,
  },

  {
    key: "licensed",
    label: "Licensed",
    type: "licenseBadge",
    center: true,
    width: 140,
    sortable: true,
  },

  // EXACTLY like 4x4: inline notes component at the end
  { key: "notes", label: "Notes", type: "notesInline", width: 320 },
];

/* ------------------ Page ------------------ */
export default function LicensingTracker() {
  const { isFeatureEnabled } = useUIVersion();
  const { user: currentUser } = useAuth();
  const { plan: accountPlan } = useAccountType();

  const modernTrackersEnabled = isFeatureEnabled("modernTrackers");
  const modernFiltersEnabled = isFeatureEnabled("modernTrackerFilters");

  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Hierarchy filtering
  const [allUsers, setAllUsers] = useState([]);
  const [allowedUserIds, setAllowedUserIds] = useState(new Set());

  const [nameQ, setNameQ] = useState("");
  const [recruiterQ, setRecruiterQ] = useState("");
  const [leaderQ, setLeaderQ] = useState("");

  // ✅ Debounce search filters to avoid immediate query firing
  const debouncedNameQ = useDebounce(nameQ, 500);
  const debouncedRecruiterQ = useDebounce(recruiterQ, 500);
  const debouncedLeaderQ = useDebounce(leaderQ, 500);
  const hasSearchFilters = useMemo(() => {
    const nq = debouncedNameQ.trim();
    const rq = debouncedRecruiterQ.trim();
    const lq = debouncedLeaderQ.trim();
    return !!(nq || rq || lq);
  }, [debouncedNameQ, debouncedRecruiterQ, debouncedLeaderQ]);
  const [sortKey, setSortKey] = useState("createdAtMillis");
  const [sortDir, setSortDir] = useState("desc");

  // Filters
  const [dateFilter, setDateFilter] = useState("all");
  const [baseShopFilter, setBaseShopFilter] = useState("all");
  const [customDateRange, setCustomDateRange] = useState({
    start: "",
    end: "",
  });

  // cursor for Firestore paging
  const pageStateRef = useRef({
    nextCursor: null,
    pageSize: 50,
    baseQueryKey: "default",
  });

  const loadRequestIdRef = useRef(0);

  // live listeners for trackers docs
  const trackerUnsubsRef = useRef(new Map()); // userId -> unsub

  // EXACTLY like 4x4
  const [notesOpenFor, setNotesOpenFor] = useState(null);
  const [noteDrafts, setNoteDrafts] = useState({});
  const [focusedNoteInputId, setFocusedNoteInputId] = useState(null);
  const [noteDeletingId, setNoteDeletingId] = useState(null);
  const [notesByEmail, setNotesByEmail] = useState({}); // All notes grouped by emailId

  const {
    id: profileId,
    openWithId: openProfile,
    close: closeProfile,
  } = useProfileModalRoute();
  const [profileData, setProfileData] = useState(null);

  /* ------------------ Auto-compute LicenseActive helpers ------------------ */
  const REQUIRED_CHECKS = [
    "xcel",
    "fingerprint",
    "licenseCert",
    "launchDirect",
    "agentAgreement",
    "continuingEd",
    "eopPlatform",
    "directDeposit",
  ];

  const REQUIRED_DATES = [
    "testDate",
    "testResultDate",
    "sirconNiprDate",
    "agentApprovalDate",
  ];

  const isFilledDate = (v) => !!toInputDate(v);

  function computeLicenseActive(rowLike) {
    const checksOk = REQUIRED_CHECKS.every((k) => rowLike[k] === true);
    const datesOk = REQUIRED_DATES.every((k) => isFilledDate(rowLike[k]));
    const passed = String(rowLike.testResult || "").toLowerCase() === "yes";
    return checksOk && datesOk && passed;
  }

  const updateRow = (rowId, patch) =>
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, ...patch } : r))
    );

  const recomputeAndPersistLicenseActive = async (
    rowBeforePatch,
    patch = {}
  ) => {
    const merged = { ...rowBeforePatch, ...patch };
    const next = computeLicenseActive(merged);
    if (merged.licenseActive !== next) {
      // optimistic UI update
      updateRow(merged.id, { licenseActive: next });
      try {
        await ensureLicensingExists(merged.id, merged);
        // ✅ Use the proper field name: licensing.licensed (not licenseActive)
        // This will auto-propagate to fourByFour.licensed and associate.getLicensed
        await syncUpdateLicensingField(merged.id, "licensing.licensed", next);
      } catch (e) {
        console.error("Error updating licensed status:", e);
      }
    }
  };

  // Fetch all users to build hierarchy
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "users"), (snap) => {
      const users = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setAllUsers(users);
    });
    return () => unsub();
  }, []);

  // Compute which users can be viewed based on account type and hierarchy
  useEffect(() => {
    if (!currentUser) {
      setAllowedUserIds(new Set());
      return;
    }
    const allowed = buildAccessibleUsers(
      accountPlan,
      allUsers,
      currentUser.uid
    );
    setAllowedUserIds(allowed);
  }, [currentUser, accountPlan, allUsers]);

  // Initial page: query trackers collection ordered by licensing.createdAt desc
  useEffect(() => {
    async function loadInitial() {
      const requestId = ++loadRequestIdRef.current;
      setLoading(true);
      setError("");

      try {
        // ✅ When search filters are active, load ALL data (no limit)
        const mapTrackerToRow = (tracker) => {
          const licensing = tracker.licensing || {};

          return {
            id: tracker.id,
            name: tracker.name || displayName(tracker),
            firstName: tracker.firstName,
            lastName: tracker.lastName,
            email: tracker.email,
            phone: tracker.phone,
            photoURL: tracker.photoURL,
            leader: tracker.leader || "—",
            recruiter: licensing.recruiter || "—",
            code: tracker.code,
            date: tracker.date,
            photo: tracker.photoURL,
            baseShopId: tracker.baseShopId || null,
            baseShop: tracker.baseShop || null,
            createdAt: tracker.createdAt || null,
            createdAtMillis: new Date(
              tracker.createdAt?.toDate?.() || tracker.createdAt
            ).getTime(),

            // Map licensing fields to row properties
            xcel: licensing.xcel || false,
            testDate: licensing.testDate || "",
            testResultDate: licensing.testResultDate || "",
            testResult: licensing.testResult || "",
            fingerprint: licensing.fingerprint || false,
            sirconNiprDate: licensing.sirconNiprDate || "",
            licenseCert: licensing.licenseCert || false,
            launchDirect: licensing.launchDirect || false,
            agentAgreement: licensing.agentAgreement || false,
            agentApprovalDate: licensing.agentApprovalDate || "",
            continuingEd: licensing.continuingEd || false,
            eopPlatform: licensing.eopPlatform || false,
            directDeposit: licensing.directDeposit || false,
            // Additional fields
            applicationSubmitted: licensing.applicationSubmitted || false,
            startedCourse: licensing.startedCourse || false,
            scheduledClass: licensing.scheduledClass || false,
            attendedClass: licensing.attendedClass || false,
            completedCourse: licensing.completedCourse || false,
            practiceExam: licensing.practiceExam || false,
            scheduledExam: licensing.scheduledExam || false,
            passedExam: licensing.passedExam || false,
            licenseActive: licensing.licensed || licensing.licenseActive || false, // ✅ Read from licensed (new field)
            notes: licensing.notes || [],
            lastNote: licensing.lastNote || null,
          };
        };

        const applyRows = (nextRows, nextCursor) => {
          setRows(nextRows);
          nextRows.forEach((r) => attachTrackerListener(r.id));
          pageStateRef.current.nextCursor = nextCursor || null;
        };

        if (hasSearchFilters) {
          const qy = query(
            collection(db, "trackers"),
            orderBy("licensing.createdAt", "desc")
          );

          const snap = await getDocs(qy);
          if (requestId !== loadRequestIdRef.current) return;

          const trackerDocs = snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            _doc: d,
          }));

          const baseRows = trackerDocs.map(mapTrackerToRow);
          applyRows(baseRows, null);
        } else {
          const pageSize = pageStateRef.current.pageSize;

          if (allowedUserIds.size === 0) {
            const qy = query(
              collection(db, "trackers"),
              orderBy("licensing.createdAt", "desc"),
              limit(pageSize)
            );

            const snap = await getDocs(qy);
            if (requestId !== loadRequestIdRef.current) return;

            const trackerDocs = snap.docs.map((d) => ({
              id: d.id,
              ...d.data(),
              _doc: d,
            }));

            const baseRows = trackerDocs.map(mapTrackerToRow);
            const last = snap.docs[snap.docs.length - 1] || null;
            applyRows(baseRows, last);
            return;
          }

          let cursor = null;
          let lastDoc = null;
          const seenIds = new Set();
          const collected = [];

          do {
            const qy = query(
              collection(db, "trackers"),
              orderBy("licensing.createdAt", "desc"),
              ...(cursor ? [startAfter(cursor)] : []),
              limit(pageSize)
            );

            const snap = await getDocs(qy);
            if (requestId !== loadRequestIdRef.current) return;

            const trackerDocs = snap.docs.map((d) => ({
              id: d.id,
              ...d.data(),
              _doc: d,
            }));

            const pageRows = trackerDocs.map(mapTrackerToRow);
            const allowedRows = pageRows.filter((r) =>
              allowedUserIds.has(r.id)
            );

            allowedRows.forEach((row) => {
              if (!seenIds.has(row.id)) {
                seenIds.add(row.id);
                collected.push(row);
              }
            });

            lastDoc = snap.docs[snap.docs.length - 1] || null;
            cursor = lastDoc;
          } while (collected.length < pageSize && lastDoc);

          applyRows(collected, lastDoc);
        }
      } catch (e) {
        if (requestId !== loadRequestIdRef.current) return;
        console.error("Error loading trackers:", e);
        setError("Failed to load licensing users.");
      } finally {
        if (requestId === loadRequestIdRef.current) setLoading(false);
      }
    }

    function attachTrackerListener(userId) {
      if (trackerUnsubsRef.current.has(userId)) return; // already listening

      const unsub = onSnapshot(trackersRef(userId), (docSnapshot) => {
        if (!docSnapshot.exists()) return;

        const trackerData = docSnapshot.data();
        const licensing = trackerData.licensing || {};

        const updates = {
          // Root fields
          name: trackerData.name || displayName(trackerData),
          firstName: trackerData.firstName,
          lastName: trackerData.lastName,
          email: trackerData.email,
          phone: trackerData.phone,
          leader: trackerData.leader || "—",
          code: trackerData.code,
          date: trackerData.date,
          baseShop: trackerData.baseShop || null,

          // licensing fields
          recruiter: licensing.recruiter || "—",
          xcel: licensing.xcel || false,
          testDate: licensing.testDate || "",
          testResultDate: licensing.testResultDate || "",
          testResult: licensing.testResult || "",
          fingerprint: licensing.fingerprint || false,
          sirconNiprDate: licensing.sirconNiprDate || "",
          licenseCert: licensing.licenseCert || false,
          launchDirect: licensing.launchDirect || false,
          agentAgreement: licensing.agentAgreement || false,
          agentApprovalDate: licensing.agentApprovalDate || "",
          continuingEd: licensing.continuingEd || false,
          eopPlatform: licensing.eopPlatform || false,
          directDeposit: licensing.directDeposit || false,
          // Additional fields
          applicationSubmitted: licensing.applicationSubmitted || false,
          startedCourse: licensing.startedCourse || false,
          scheduledClass: licensing.scheduledClass || false,
          attendedClass: licensing.attendedClass || false,
          completedCourse: licensing.completedCourse || false,
          practiceExam: licensing.practiceExam || false,
          scheduledExam: licensing.scheduledExam || false,
          passedExam: licensing.passedExam || false,
          licenseActive: licensing.licensed || licensing.licenseActive || false, // ✅ Read from licensed (new field)
          notes: licensing.notes || [],
          lastNote: licensing.lastNote || null,
        };

        setRows((prev) =>
          prev.map((row) => (row.id === userId ? { ...row, ...updates } : row))
        );
      });

      trackerUnsubsRef.current.set(userId, unsub);
    }

    loadInitial();

    return () => {
      loadRequestIdRef.current += 1;
      // cleanup all listeners
      for (const [, unsub] of trackerUnsubsRef.current)
        try {
          unsub();
        } catch {}
      trackerUnsubsRef.current.clear();
    };
  }, [allowedUserIds, hasSearchFilters]);

  // Load more (next page of trackers)
  const loadMore = useCallback(async () => {
    const cursor = pageStateRef.current.nextCursor;
    if (!cursor || hasSearchFilters) return;
    setLoadingMore(true);
    try {
      const qy = query(
        collection(db, "trackers"),
        orderBy("licensing.createdAt", "desc"),
        startAfter(cursor),
        limit(pageStateRef.current.pageSize)
      );

      const snap = await getDocs(qy);
      const newTrackers = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        _doc: d,
      }));

      const moreRows = newTrackers.map((tracker) => {
        const licensing = tracker.licensing || {};

        return {
          id: tracker.id,
          name: tracker.name || displayName(tracker),
          firstName: tracker.firstName,
          lastName: tracker.lastName,
          email: tracker.email,
          phone: tracker.phone,
          photoURL: tracker.photoURL,
          leader: tracker.leader || "—",
          recruiter: licensing.recruiter || "—",
          code: tracker.code,
          date: tracker.date,
          photo: tracker.photoURL,
          baseShopId: tracker.baseShopId || null,
          baseShop: tracker.baseShop || null,
          createdAt: tracker.createdAt || null,
          createdAtMillis: new Date(
            tracker.createdAt?.toDate?.() || tracker.createdAt
          ).getTime(),

          // Map licensing fields
          xcel: licensing.xcel || false,
          testDate: licensing.testDate || "",
          testResultDate: licensing.testResultDate || "",
          testResult: licensing.testResult || "",
          fingerprint: licensing.fingerprint || false,
          sirconNiprDate: licensing.sirconNiprDate || "",
          licenseCert: licensing.licenseCert || false,
          launchDirect: licensing.launchDirect || false,
          agentAgreement: licensing.agentAgreement || false,
          agentApprovalDate: licensing.agentApprovalDate || "",
          continuingEd: licensing.continuingEd || false,
          eopPlatform: licensing.eopPlatform || false,
          directDeposit: licensing.directDeposit || false,
          applicationSubmitted: licensing.applicationSubmitted || false,
          startedCourse: licensing.startedCourse || false,
          scheduledClass: licensing.scheduledClass || false,
          attendedClass: licensing.attendedClass || false,
          completedCourse: licensing.completedCourse || false,
          practiceExam: licensing.practiceExam || false,
          scheduledExam: licensing.scheduledExam || false,
          passedExam: licensing.passedExam || false,
          licenseActive: licensing.licensed || licensing.licenseActive || false, // ✅ Read from licensed (new field)
          notes: licensing.notes || [],
          lastNote: licensing.lastNote || null,
        };
      });

      setRows((prev) => {
        const ids = new Set(prev.map((x) => x.id));
        const deduped = [...prev, ...moreRows.filter((r) => !ids.has(r.id))];
        // attach listeners for new ones
        moreRows.forEach((r) => {
          if (!trackerUnsubsRef.current.has(r.id)) {
            const unsub = onSnapshot(trackersRef(r.id), (docSnapshot) => {
              if (!docSnapshot.exists()) return;

              const trackerData = docSnapshot.data();
              const licensing = trackerData.licensing || {};

              const updates = {
                name: trackerData.name || displayName(trackerData),
                firstName: trackerData.firstName,
                lastName: trackerData.lastName,
                email: trackerData.email,
                phone: trackerData.phone,
                leader: trackerData.leader || "—",
                code: trackerData.code,
                date: trackerData.date,
                baseShop: trackerData.baseShop || null,
                recruiter: licensing.recruiter || "—",
                xcel: licensing.xcel || false,
                testDate: licensing.testDate || "",
                testResultDate: licensing.testResultDate || "",
                testResult: licensing.testResult || "",
                fingerprint: licensing.fingerprint || false,
                sirconNiprDate: licensing.sirconNiprDate || "",
                licenseCert: licensing.licenseCert || false,
                launchDirect: licensing.launchDirect || false,
                agentAgreement: licensing.agentAgreement || false,
                agentApprovalDate: licensing.agentApprovalDate || "",
                continuingEd: licensing.continuingEd || false,
                eopPlatform: licensing.eopPlatform || false,
                directDeposit: licensing.directDeposit || false,
                applicationSubmitted: licensing.applicationSubmitted || false,
                startedCourse: licensing.startedCourse || false,
                scheduledClass: licensing.scheduledClass || false,
                attendedClass: licensing.attendedClass || false,
                completedCourse: licensing.completedCourse || false,
                practiceExam: licensing.practiceExam || false,
                scheduledExam: licensing.scheduledExam || false,
                passedExam: licensing.passedExam || false,
                licenseActive: licensing.licensed || licensing.licenseActive || false, // ✅ Read from licensed (new field)
                notes: licensing.notes || [],
                lastNote: licensing.lastNote || null,
              };

              setRows((p) =>
                p.map((row) => (row.id === r.id ? { ...row, ...updates } : row))
              );
            });
            trackerUnsubsRef.current.set(r.id, unsub);
          }
        });
        return deduped;
      });

      const last = snap.docs[snap.docs.length - 1] || null;
      pageStateRef.current.nextCursor = last || null;
    } catch (e) {
      console.error("Error loading more trackers:", e);
      setError("Failed to load more users.");
    } finally {
      setLoadingMore(false);
    }
  }, [hasSearchFilters]);

  useEffect(() => {
    if (!profileId) return setProfileData(null);
    const row = rows.find((r) => r.id === profileId);
    if (row)
      setProfileData({
        photoUrl: row.photoURL,
        name: row.name,
        recruiter: row.recruiter,
        leader: row.leader,
        agencyCode: row.code,
        amaDate: row.date,
        notes: row.notes,
      });
  }, [profileId, rows]);

  // Direct checkbox toggle — leaf-only write, no reinit of licensing block
  const toggleCheckbox = useCallback(async (row, fieldName) => {
    const rowId = row.id;
    const currentValue = !!row[fieldName];
    const nextValue = !currentValue;

    // Optimistic UI update
    updateRow(rowId, { [fieldName]: nextValue });

    try {
      await updateCheckboxField(rowId, fieldName, nextValue, row);
      await recomputeAndPersistLicenseActive({
        ...row,
        [fieldName]: nextValue,
      });
    } catch (e) {
      console.error(`Error toggling ${fieldName}:`, e);
      // Revert optimistic update on error
      updateRow(rowId, { [fieldName]: currentValue });
    }
  }, []);

  // Safe date update function
  const updateLicensingDate = useCallback(
    async (row, fieldName, value) => {
      const rowId = row.id;

      // Optimistic UI update
      updateRow(rowId, { [fieldName]: value });

      try {
        await ensureLicensingExists(
          rowId,
          rows.find((r) => r.id === rowId)
        );
        
        // ✅ Check if this is a date field that should mirror to 4x4
        const dateFieldsToMirror = ["sirconNiprDate", "testResultDate", "passExamDate"];
        const shouldMirror = dateFieldsToMirror.includes(fieldName);
        
        if (shouldMirror) {
          // ✅ Use the sync function that mirrors dates to 4x4
          await syncUpdateLicensingDate(rowId, `licensing.${fieldName}`, value);
        } else {
          // Regular date field (no mirroring)
          await syncUpdateLicensingField(rowId, `licensing.${fieldName}`, value);
        }
        
        await recomputeAndPersistLicenseActive({ ...row, [fieldName]: value });
      } catch (e) {
        console.error("Error updating licensing date:", e);
        // Revert optimistic update on error
        updateRow(rowId, { [fieldName]: row[fieldName] });
      }
    },
    [rows]
  );

  const filteredRows = useMemo(() => {
    let filtered = rows;

    const nq = debouncedNameQ.trim().toLowerCase();
    const rq = debouncedRecruiterQ.trim().toLowerCase();
    const lq = debouncedLeaderQ.trim().toLowerCase();
    // ✅ Apply hierarchy filter ONLY when no search filters are active
    if (!hasSearchFilters) {
      filtered = filtered.filter((r) => allowedUserIds.has(r.id));
    }

    if (hasSearchFilters) {
      filtered = filtered.filter(
        (r) =>
          (nq ? (r.name || "").toLowerCase().includes(nq) : true) &&
          (rq ? (r.recruiter || "").toLowerCase().includes(rq) : true) &&
          (lq ? (r.leader || "").toLowerCase().includes(lq) : true)
      );
    }

    if (modernFiltersEnabled) {
      filtered = filterByDateRange(
        filtered,
        dateFilter,
        customDateRange,
        "createdAt"
      );
      filtered = filterByBaseShop(filtered, baseShopFilter, currentUser, {
        recruiterField: "recruiter",
        leaderField: "leader",
      });
    }
    return filtered;
  }, [
    rows,
    debouncedNameQ,
    debouncedRecruiterQ,
    debouncedLeaderQ,
    hasSearchFilters,
    modernFiltersEnabled,
    dateFilter,
    baseShopFilter,
    customDateRange,
    currentUser,
    allowedUserIds,
  ]);

  const sorted = useMemo(() => {
    const normalize = (val) => {
      if (val === null || val === undefined) return "";
      if (typeof val === "number") return val;
      if (typeof val === "boolean") return val ? 1 : 0;
      if (val instanceof Date) return val.getTime();
      const maybeTs = tsToMs(val);
      if (Number.isFinite(maybeTs) && maybeTs > 0) return maybeTs;

      const str = val.toString ? val.toString() : "";
      const parsed = Date.parse(str);
      if (Number.isFinite(parsed)) return parsed;
      return str.toLowerCase();
    };

    const arr = [...filteredRows];
    arr.sort((a, b) => {
      const va = normalize(a[sortKey]);
      const vb = normalize(b[sortKey]);

      if (typeof va === "number" && typeof vb === "number") {
        return sortDir === "asc" ? va - vb : vb - va;
      }

      const cmp = String(va).localeCompare(String(vb));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filteredRows, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  // Subscribe to centralized notes by prospectId (includes all tracker notes)
  useEffect(() => {
    if (rows.length === 0) return;

    const unsubscribers = rows.map(row => {
      if (!row.id) return () => {};

      return subscribeToProspectNotes(row.id, (notes) => {
        setNotesByEmail(prev => ({
          ...prev,
          [row.id]: notes,
        }));
      });
    });

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [rows.length]);

  const openNotes = (row) => setNotesOpenFor(row);
  const closeNotes = () => setNotesOpenFor(null);

  // Delete note function
  const deleteNote = async (row, noteId) => {
    if (!row) return;
    setNoteDeletingId(noteId);
    try {
      // Optimistically update local cache
      if (row.email) {
        setNotesByEmail(prev => {
          const existing = prev[row.email] || [];
          return {
            ...prev,
            [row.email]: existing.filter(n => n.id !== noteId),
          };
        });
      }

      await deleteNoteService(noteId);
    } catch (e) {
      console.error("Error deleting note:", e);
    } finally {
      setNoteDeletingId(null);
    }
  };

  // Get current notes for modal
  const currentNotesForOpenRow = useMemo(() => {
    if (!notesOpenFor) return [];
    const allNotes = notesByEmail[notesOpenFor.id] || [];
    const sorted = [...allNotes].sort((a, b) => tsToMs(a.createdAt) - tsToMs(b.createdAt));
    return sorted;
  }, [notesOpenFor, notesByEmail]);

  const sortedModalNotes = useMemo(() => {
    const copy = [...currentNotesForOpenRow];
    copy.sort((a, b) => tsToMs(a.createdAt) - tsToMs(b.createdAt));
    return copy;
  }, [currentNotesForOpenRow]);

  // Add inline note function
  const addInlineNote = async (row) => {
    const text = (noteDrafts[row.id] || "").trim();
    if (!text) return;
    try {
      const recruiterName = auth?.currentUser?.displayName || auth?.currentUser?.email || "Me";

      // Use centralized notes service
      await addNote({
        prospectId: row.id,
        trackerId: row.id,
        emailId: row.id,
        text: text,
        createdBy: recruiterName,
        tracker: "licensing",
      });

      // Clear the draft
      setNoteDrafts((d) => ({ ...d, [row.id]: "" }));
    } catch (e) {
      console.error("Error adding note:", e);
    }
  };

  // Licensed count based on licenseActive field
  const licensedCount = useMemo(
    () => filteredRows.filter((r) => r.licenseActive === true).length,
    [filteredRows]
  );

  // Filters header
  const filtersComponent = (
    <div className="tracker-filter-row">
      <div className="tracker-metric-card">
        <div className="metric-icon">
          <IconUsers size={20} stroke={1.7} />
        </div>
        <div className="metric-copy">
          <span className="metric-label">Total People</span>
          <span className="metric-value">{filteredRows.length}</span>
        </div>
      </div>
      <div className="tracker-metric-card">
        <div className="metric-icon">
          <IconLicense size={20} stroke={1.7} />
        </div>
        <div className="metric-copy">
          <span className="metric-label">Licensed</span>
          <span className="metric-value">{licensedCount}</span>
        </div>
      </div>
      <TrackerFilters
        users={rows}
        currentUser={currentUser}
        onRangeChange={(range) => {
          if (range) {
            setDateFilter("custom");
            setCustomDateRange(range);
          } else {
            setDateFilter("all");
            setCustomDateRange(null);
          }
        }}
        onBaseShopChange={setBaseShopFilter}
        defaultRange="all"
        defaultBaseShop="all"
        className="tracker-filter-controls"
        layout="inline"
      />
    </div>
  );

  const TrackerWrapper = modernTrackersEnabled
    ? TrackerContainer
    : TrackerShell;
  const wrapperProps = modernTrackersEnabled
    ? {
        title: "Licensing Tracker",
        trackerType: "licensing",
        totalCount: null,
        showFilters: true,
        filterComponent: filtersComponent,
      }
    : { title: "Licensing Tracker" };

  // ---------- CELL RENDERER ----------
  const renderCell = (row, col, rowIndex) => {
    if (col.key === "index") {
      return (
        <div
          className="index-cell"
          style={{
            fontWeight: 700,
            color: "#f3d67a",
            textAlign: "center",
            fontSize: "14px",
          }}
        >
          {rowIndex + 1}
        </div>
      );
    }

    if (col.key === "name") {
      const hasPhoto = !!(row.photoURL || row.avatarUrl || row.photo);
      const ama = fmtDate(row.date);
      const code = row.code || "";
      const sub = [ama, code].filter(Boolean).join(" • ");
      return (
        <div className="name-like-cell">
          <AvatarSquare
            row={row}
            size={32}
            onClick={() => openProfile(row.id)}
          />
          <div
            className="name-text-wrap"
            style={{ display: "flex", flexDirection: "column" }}
          >
            <div
              className="name-line"
              style={{
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              {!hasPhoto && (
                <span
                  className="name-star"
                  style={{ color: "#f3d67a", fontWeight: 900 }}
                >
                  *
                </span>
              )}
              <span>{row.name || "—"}</span>
            </div>
            {!!sub && (
              <div className="name-sub" style={{ fontSize: 12, opacity: 0.8 }}>
                {sub}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (["recruiter", "leader"].includes(col.key)) {
      const text = row[col.key] || "—";
      return (
        <div
          className="cell-with-btn"
          style={{ display: "flex", alignItems: "center", gap: 10 }}
        >
          <button
            type="button"
            className="profile-mini"
            title="Open profile"
            onClick={() => openProfile(row.id)}
            style={{
              width: 26,
              height: 26,
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid rgba(255,255,255,.18)",
              background: "rgba(255,255,255,.06)",
              cursor: "pointer",
              color: "#fff",
              fontSize: "12px",
            }}
          >
            👤
          </button>
          <div className="cell-text">{text}</div>
        </div>
      );
    }

    // Date inputs
    if (col.type === "date") {
      const value = toInputDate(row[col.key]);
      return (
        <input
          type="date"
          className="date-input"
          value={value}
          onChange={(e) => updateLicensingDate(row, col.key, e.target.value)}
        />
      );
    }

    // Test Result (date + yes/no)
    if (col.type === "resultCombo") {
      return (
        <div className="result-wrap">
          <input
            type="date"
            className="date-input"
            value={toInputDate(row.testResultDate)}
            onChange={(e) =>
              updateLicensingDate(row, "testResultDate", e.target.value)
            }
          />
          <select
            className="yesno-select"
            value={row.testResult || ""}
            onChange={async (e) => {
              const newVal = e.target.value;
              updateRow(row.id, { testResult: newVal }); // keep UI in sync
              try {
                await updateLicensingField(
                  row.id,
                  "licensing.testResult",
                  newVal
                );
                await recomputeAndPersistLicenseActive({
                  ...row,
                  testResult: newVal,
                });
              } catch (error) {
                console.error("Error updating test result:", error);
              }
            }}
          >
            <option value=""></option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </div>
      );
    }

    // Checks - use the simple toggle function for ALL checkboxes (leaf-only)
    if (col.type === "check") {
      const checked = !!row[col.key];
      return (
        <div
          className={`light-cell ${checked ? "green" : "red"}`}
          onClick={() => toggleCheckbox(row, col.key)}
        >
          {checked ? "Done" : "Pending"}
        </div>
      );
    }

    // Licensed badge
    if (col.type === "licenseBadge") {
      const isLicensed = row.licenseActive === true;
      return (
        <div className={`license-badge ${isLicensed ? "yes" : "no"}`}>
          {isLicensed ? "Yes" : "No"}
        </div>
      );
    }

    // Inline notes with ProspectTracker style
    if (col.type === "notesInline" || col.key === "notes") {
      const rid = row.id;
      const notesArr = notesByEmail[row.id] || [];
      const lastNote =
        notesArr.length > 0
          ? notesArr
              .slice()
              .reverse()
              .find((n) => n.text)
          : null;
      
      const isNoteFocused = focusedNoteInputId === rid;
      
      // Show latest note in the input when not focused, or draft when focused/typing
      const inputValue = isNoteFocused 
        ? (noteDrafts[rid] || "")
        : (noteDrafts[rid] || (lastNote?.text || ""));
      
      return (
        <div className="notes-cell-content">
          <div className="notes-inline-add">
            <input
              className={`note-input ${!isNoteFocused && lastNote && !noteDrafts[rid] ? "showing-existing" : ""}`}
              type="text"
              placeholder={lastNote ? "Add a new note... (Press Enter)" : "Add a quick note... (Press Enter)"}
              value={inputValue}
              onChange={(e) =>
                setNoteDrafts((d) => ({ ...d, [rid]: e.target.value }))
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && (noteDrafts[rid] || "").trim()) {
                  addInlineNote(row);
                }
              }}
              onFocus={() => {
                setFocusedNoteInputId(rid);
                // Clear the input when user focuses to type new note
                if (!noteDrafts[rid]) {
                  setNoteDrafts((d) => ({ ...d, [rid]: "" }));
                }
              }}
              onBlur={() => setFocusedNoteInputId(null)}
            />
            <button
              className="notes-openall-emoji"
              onClick={() => setNotesOpenFor(row)}
              title="Open all notes"
              type="button"
            >
              ✏️
            </button>
          </div>
          
          {/* Show note metadata below when showing existing note */}
          {!isNoteFocused && lastNote && !noteDrafts[rid] && (
            <div className="note-meta-under">
              {lastNote.createdBy || "—"} • {
                lastNote.createdAtISO
                  ? formatDateTimePretty(lastNote.createdAtISO)
                  : tsToMs(lastNote.createdAt)
                  ? formatDateTimePretty(
                      new Date(tsToMs(lastNote.createdAt)).toISOString()
                    )
                  : ""
              }
            </div>
          )}
        </div>
      );
    }

    return "—";
  };

  return (
    <TooltipProvider>
      <TrackerWrapper {...wrapperProps}>
        {/* GRID */}
        <ResizableGrid
          columns={COLS}
          rows={sorted}
          loading={loading}
          emptyText="No data found."
          renderCell={renderCell}
          rowKey={(r) => r.id}
          lockableLeftCount={4}
          initialLockedKeys={["index", "name", "recruiter", "leader"]}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={handleSort}
          renderHeaderSearch={(col) => {
            if (col.key === "name")
              return (
                <input
                  className="mini-search"
                  placeholder="Search Name…"
                  value={nameQ}
                  onChange={(e) => setNameQ(e.target.value)}
                />
              );
            if (col.key === "recruiter")
              return (
                <input
                  className="mini-search"
                  placeholder="Search Recruiter…"
                  value={recruiterQ}
                  onChange={(e) => setRecruiterQ(e.target.value)}
                />
              );
            if (col.key === "leader")
              return (
                <input
                  className="mini-search"
                  placeholder="Search Leader…"
                  value={leaderQ}
                  onChange={(e) => setLeaderQ(e.target.value)}
                />
              );
            return null;
          }}
        />

        {/* Firestore paging (load next 50) */}
        <div
          style={{ display: "flex", justifyContent: "center", marginTop: 16 }}
        >
          <button
            className="btn"
            onClick={loadMore}
            disabled={loadingMore || !pageStateRef.current.nextCursor}
          >
            {pageStateRef.current.nextCursor
              ? loadingMore
                ? "Loading…"
                : "Load more users"
              : "All loaded"}
          </button>
        </div>

        {/* Profile card */}
        <UserProfileCard
          open={!!profileId && !!profileData}
          onClose={closeProfile}
          person={profileData || {}}
        />
      </TrackerWrapper>

      {/* Notes Modal */}
      {notesOpenFor && (
        <div className="modal-backdrop" onClick={() => setNotesOpenFor(null)}>
          <div
            className="modal-card"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <div className="modal-head">
              <h3>Notes — {displayName(notesOpenFor)}</h3>
              <button
                className="btn ghost"
                onClick={() => setNotesOpenFor(null)}
              >
                ✕
              </button>
            </div>
            <div className="notes-list-modal">
              {sortedModalNotes.length === 0 && (
                <div className="empty-notes">No notes yet.</div>
              )}
              {sortedModalNotes.map((n) => (
                <div key={n.id} className="note-row-modal">
                  <div className="note-text-modal">{n.text}</div>
                  <div className="note-meta-modal">
                    {n.createdBy || "—"} •{" "}
                    {n.createdAtISO
                      ? formatDateTimeISO(n.createdAtISO)
                      : tsToMs(n.createdAt)
                      ? formatDateTimeISO(
                          new Date(tsToMs(n.createdAt)).toISOString()
                        )
                      : ""}
                  </div>
                  <button
                    className="note-delete-modal"
                    onClick={() => deleteNote(notesOpenFor, n.id)}
                    disabled={noteDeletingId === n.id}
                    title="Delete note"
                  >
                    {noteDeletingId === n.id ? "…" : "Delete"}
                  </button>
                </div>
              ))}
            </div>
            <div className="notes-modal-add">
              <textarea
                className="input"
                rows="2"
                placeholder="Add a new note..."
                value={noteDrafts["modal"] || ""}
                onChange={(e) =>
                  setNoteDrafts((d) => ({ ...d, modal: e.target.value }))
                }
              ></textarea>
              <button
                className="btn primary"
                onClick={async () => {
                  const text = (noteDrafts["modal"] || "").trim();
                  if (!text) return;
                  
                  const recruiterName = auth?.currentUser?.displayName || auth?.currentUser?.email || "Me";
                  
                  try {
                    await addNote({
                      prospectId: notesOpenFor.id,
                      trackerId: notesOpenFor.id,
                      emailId: notesOpenFor.id,
                      text: text,
                      createdBy: recruiterName,
                      tracker: "licensing",
                    });
                    setNoteDrafts((d) => ({ ...d, modal: "" }));
                  } catch (e) {
                    console.error("Error updating notes:", e);
                  }
                }}
              >
                Add Note
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes Cell Styles */}
      <style>{`
        .notes-cell-content {
          display: flex;
          flex-direction: column;
          gap: 4px;
          width: 100%;
          align-items: flex-start;
        }

        .notes-inline-add {
          display: flex;
          align-items: center;
          gap: 6px;
          width: 100%;
        }

        .note-input {
          flex: 1 1 auto;
          min-width: 120px;
          background: rgba(255, 255, 255, 0.08);
          color: #fff;
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          padding: 6px 10px;
          transition: all 0.2s ease;
        }

        .note-input.showing-existing {
          background: rgba(255, 215, 0, 0.08);
          border-left: 3px solid rgba(255, 215, 0, 0.4);
          padding-left: 8px;
          font-style: italic;
          opacity: 0.95;
        }

        .notes-openall-emoji {
          background: rgba(255, 255, 255, 0.08);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          padding: 6px 8px;
          cursor: pointer;
        }

        .note-meta-under {
          font-size: 11px;
          opacity: 0.8;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Notes Modal Styles - ProspectTracker style */
        .modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .modal-card {
          background: #1f2128;
          color: #fff;
          border: 1px solid #3a3f4a;
          border-radius: 16px;
          box-shadow: 0 14px 40px rgba(0,0,0,.45);
          padding: 16px 16px 12px;
          max-height: 92vh;
          overflow: auto;
          width: min(860px,94vw);
        }
        
        .modal-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 8px;
        }
        
        .modal-body {
          display: block;
        }
        
        .btn {
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.18);
          color: #fff;
          border-radius: 10px;
          padding: 8px 12px;
          cursor: pointer;
        }
        
        .btn.primary {
          background: rgba(255,215,0,.2);
          color: #ffd700;
          border-color: rgba(255,215,0,.35);
        }
        
        .btn.ghost {
          background: transparent;
          border-color: rgba(255,255,255,.25);
        }
        
        .input {
          width: 100%;
          box-sizing: border-box;
          background: rgba(255,255,255,.06);
          color: #fff;
          border: 1px solid rgba(255,255,255,.15);
          border-radius: 10px;
          padding: 10px 12px;
          outline: none;
        }
        
        .input:focus {
          border-color: rgba(255,215,0,.55);
          box-shadow: 0 0 0 2px rgba(255,215,0,.12) inset;
        }
        
        .notes-list-modal {
          max-height: 40vh;
          overflow: auto;
          background: rgba(255,255,255,.04);
          border: 1px solid rgba(255,255,255,.08);
          border-radius: 12px;
          padding: 8px;
          margin-bottom: 12px;
        }
        
        .empty-notes {
          opacity: .7;
          text-align: center;
          padding: 18px 8px;
        }
        
        .note-row-modal {
          display: grid;
          grid-template-columns: 1fr auto auto;
          gap: 10px;
          align-items: center;
          padding: 8px;
          border-radius: 10px;
        }
        
        .note-row-modal:not(:last-child) {
          border-bottom: 1px dashed rgba(255,255,255,.08);
        }
        
        .note-text-modal {
          white-space: pre-wrap;
        }
        
        .note-meta-modal {
          opacity: .8;
          font-size: 12px;
        }
        
        .note-delete-modal {
          background: transparent;
          border: 1px dashed rgba(255,100,100,.4);
          color: rgba(255,100,100,.9);
          border-radius: 8px;
          padding: 4px 8px;
          cursor: pointer;
        }
        
        .notes-modal-add {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
      `}</style>
    </TooltipProvider>
  );
}
